import React, { useState, useEffect } from "react";
import { AlertCircle, CreditCard, Users, FileText, Check } from "lucide-react";
import { realAPI } from "../../services/realAPI";
import {
  ProgressivePricingPlan,
  ProgressivePricingType,
  ProgressivePricingTire,
} from "../../types/types";

interface PlanSubscriptionProps {
  onUpdate?: () => void;
}

const PlanSubscription: React.FC<PlanSubscriptionProps> = ({ onUpdate }) => {
  const [progressivePlans, setProgressivePlans] = useState<
    ProgressivePricingPlan[]
  >([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setIsLoading(true);
    setError(null);

    try {
      // Load progressive plans
      const plansResponse = await realAPI.getAvailableProgressivePlans();

      console.log("Plans response:", plansResponse);

      if (plansResponse?.data?.plans) {
        // Filter only active plans
        const activePlans = plansResponse.data.plans.filter(
          (plan: ProgressivePricingPlan) => plan.isActive
        );
        setProgressivePlans(activePlans);
      }
    } catch (err) {
      console.error("Error loading plan data:", err);
      setError("Failed to load plan information. Please try again later.");
    } finally {
      setIsLoading(false);
    }
  };

  // Removed subscription functionality - this is now just for displaying available plans

  const groupPlansByType = () => {
    const storyPlans = progressivePlans
      .filter((plan) => plan.type === ProgressivePricingType.STORIES)
      .sort((a, b) => a.planLimit - b.planLimit);

    const userPlans = progressivePlans
      .filter((plan) => plan.type === ProgressivePricingType.USERS)
      .sort((a, b) => a.planLimit - b.planLimit);

    return { storyPlans, userPlans };
  };

  const getTierColor = (tire: ProgressivePricingTire) => {
    switch (tire) {
      case ProgressivePricingTire.FREE:
        return "bg-gray-100 text-gray-600";
      case ProgressivePricingTire.TOPUP_5:
      case ProgressivePricingTire.TOPUP_10:
        return "bg-blue-100 text-blue-800";
      case ProgressivePricingTire.TOPUP_20:
      case ProgressivePricingTire.TOPUP_30:
        return "bg-purple-100 text-purple-800";
      case ProgressivePricingTire.TOPUP_40:
      case ProgressivePricingTire.TOPUP_50:
        return "bg-orange-100 text-orange-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  if (isLoading) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mr-3"></div>
          <span className="text-gray-600">Loading plan information...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6">
          <div className="flex items-center">
            <AlertCircle className="h-6 w-6 text-red-500 mr-3" />
            <div>
              <h3 className="text-lg font-medium text-red-800">Error</h3>
              <p className="text-red-700 mt-1">{error}</p>
              <button
                onClick={loadData}
                className="mt-3 text-sm bg-red-100 text-red-800 px-3 py-1 rounded hover:bg-red-200"
              >
                Try Again
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const { storyPlans, userPlans } = groupPlansByType();

  return (
    <div className="p-6">
      {/* Header */}

      {/* Info Message */}
      {/* <div className="mb-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-center">
          <AlertCircle className="h-5 w-5 text-blue-500 mr-3" />
          <span className="text-blue-800">
            Available topup plans. For quota management and purchases, visit the
            Quotas & Balance section.
          </span>
        </div>
      </div> */}

      {/* Current Organization Info */}
      <h2 className="text-lg font-semibold text-gray-900 mb-4">
        Below are the available topup plans.
      </h2>
      {/* Available Plans */}
      <div className="space-y-8">
        {/* Story Plans */}
        {storyPlans.length > 0 && (
          <div>
            <div className="flex items-center mb-4">
              <FileText className="h-6 w-6 text-gray-600 mr-2" />
              <h2 className="text-xl font-semibold text-gray-900">
                Available Story Plans
              </h2>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {storyPlans.map((plan) => (
                <div
                  key={plan.id}
                  className="bg-white border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow"
                >
                  <div className="flex items-center justify-between mb-3">
                    <span
                      className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${getTierColor(
                        plan.tire
                      )}`}
                    >
                      {plan.tire}
                    </span>
                    <span className="text-2xl font-bold text-gray-900">
                      {plan.priceCents === 0
                        ? "Free"
                        : `$${(plan.priceCents / 100).toFixed(0)}`}
                    </span>
                  </div>

                  <h3 className="font-semibold text-lg text-gray-900 mb-2">
                    {plan.planLimit} Stories
                  </h3>

                  <p className="text-gray-600 text-sm mb-4">{plan.planKey}</p>

                  <div className="space-y-2 mb-4">
                    <div className="flex items-center text-sm text-gray-600">
                      <Check className="h-4 w-4 text-green-500 mr-2" />
                      Up to {plan.planLimit} stories
                    </div>
                    <div className="flex items-center text-sm text-gray-600">
                      <Check className="h-4 w-4 text-green-500 mr-2" />
                      {plan.type} management
                    </div>
                  </div>

                  <div
                    className={`w-full py-2 px-4 rounded-lg font-medium text-center ${
                      plan.priceCents === 0
                        ? "bg-green-100 text-green-700"
                        : "bg-blue-100 text-blue-700"
                    }`}
                  >
                    {plan.priceCents === 0 ? "Default Plan" : "Topup Available"}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* User Plans */}
        {userPlans.length > 0 && (
          <div>
            <div className="flex items-center mb-4">
              <Users className="h-6 w-6 text-gray-600 mr-2" />
              <h2 className="text-xl font-semibold text-gray-900">
                Available User Plans
              </h2>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {userPlans.map((plan) => (
                <div
                  key={plan.id}
                  className="bg-white border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow"
                >
                  <div className="flex items-center justify-between mb-3">
                    <span
                      className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${getTierColor(
                        plan.tire
                      )}`}
                    >
                      {plan.tire}
                    </span>
                    <span className="text-2xl font-bold text-gray-900">
                      {plan.priceCents === 0
                        ? "Free"
                        : `$${(plan.priceCents / 100).toFixed(0)}`}
                    </span>
                  </div>

                  <h3 className="font-semibold text-lg text-gray-900 mb-2">
                    {plan.planLimit} Users
                  </h3>

                  <p className="text-gray-600 text-sm mb-4">{plan.planKey}</p>

                  <div className="space-y-2 mb-4">
                    <div className="flex items-center text-sm text-gray-600">
                      <Check className="h-4 w-4 text-green-500 mr-2" />
                      Up to {plan.planLimit} users
                    </div>
                    <div className="flex items-center text-sm text-gray-600">
                      <Check className="h-4 w-4 text-green-500 mr-2" />
                      {plan.type} management
                    </div>
                  </div>

                  <div
                    className={`w-full py-2 px-4 rounded-lg font-medium text-center ${
                      plan.priceCents === 0
                        ? "bg-green-100 text-green-700"
                        : "bg-blue-100 text-blue-700"
                    }`}
                  >
                    {plan.priceCents === 0 ? "Default Plan" : "Topup Available"}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* No Plans Available */}
        {storyPlans.length === 0 && userPlans.length === 0 && (
          <div className="text-center py-12">
            <CreditCard className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              No Plans Available
            </h3>
            <p className="text-gray-600">
              There are currently no active plans available for subscription.
            </p>
            <button
              onClick={loadData}
              className="mt-4 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
            >
              Refresh
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default PlanSubscription;
